import React, { useEffect, useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import axios from 'axios';

const CartSection = () => {
    const { refreshCounts } = useCart();
    const [cartItems, setCartItems] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [totalPrice, setTotalPrice] = useState(0);
    const [expandedItems, setExpandedItems] = useState({});

    // ✅ NEW: Image states
    const [editingImageFor, setEditingImageFor] = useState(null); // cart_item_id being edited
    const [newImages, setNewImages] = useState({}); // { cart_item_id: { file, preview } }
    const [uploadingImages, setUploadingImages] = useState({}); // { cart_item_id: boolean }
    const [previewImage, setPreviewImage] = useState(null); // For popup preview


    const API_BASE_URL = process.env.REACT_APP_API_URL;
    const IMG_BASE_URL = process.env.REACT_APP_IMG_BASE_URL;

    const getImageUrl = (imageUrl) => {
        if (!imageUrl) return 'assets/images/thumbs/default-material.png';
        if (imageUrl.startsWith('http')) return imageUrl;
        if (imageUrl.startsWith('/')) return `${IMG_BASE_URL}${imageUrl}`;
        return `${IMG_BASE_URL}${imageUrl}`;
    };

    const fetchCart = useCallback(async () => {
        try {
            setIsLoading(true);
            const accessToken = localStorage.getItem('access_token');

            if (!accessToken) {
                setCartItems([]);
                setError('Please login to view your cart');
                setIsLoading(false);
                return;
            }

            const response = await axios.get(
                `${API_BASE_URL}/cart/my-cart/`,
                {
                    headers: {
                        'Authorization': `Bearer ${accessToken}`
                    }
                }
            );

            if (response.data.success) {
                const items = response.data.items || [];
                setCartItems(items);
                setTotalPrice(response.data.total_price || 0);
                setError(null);
                // Refresh the cart count in the header
                refreshCounts();
            }
        } catch (error) {
            console.error('Error fetching cart:', error);
            if (error.response?.status === 401) {
                setError('Please login to view your cart');
            } else {
                setError('Failed to load cart');
            }
            setCartItems([]);
        } finally {
            setIsLoading(false);
        }
    }, [API_BASE_URL, refreshCounts]);

    useEffect(() => {
        fetchCart();
    }, [fetchCart]);

    // ✅ NEW: Handle image selection for editing
    const handleImageEdit = (e, cartItemId) => {
        const file = e.target.files[0];
        if (file) {
            if (file.size > 5 * 1024 * 1024) {
                alert('Image size must be less than 5MB');
                return;
            }
            if (!file.type.startsWith('image/')) {
                alert('Please select a valid image file');
                return;
            }

            const reader = new FileReader();
            reader.onloadend = () => {
                setNewImages(prev => ({
                    ...prev,
                    [cartItemId]: {
                        file: file,
                        preview: reader.result
                    }
                }));
            };
            reader.readAsDataURL(file);
        }
    };

    // Replace the uploadAndUpdateImage function with this:

    const uploadAndUpdateImage = async (cartItemId) => {
        const newImageData = newImages[cartItemId];
        if (!newImageData || !newImageData.file) return;

        // Find the cart item index for local update later
        const cartItemIndex = cartItems.findIndex(item => item.cart_item_id === cartItemId);
        if (cartItemIndex === -1) return;

        setUploadingImages(prev => ({ ...prev, [cartItemId]: true }));

        try {
            const accessToken = localStorage.getItem('access_token');

            if (cartItems[cartItemIndex].customization_image_id) {
                // Update existing image
                const formData = new FormData();
                formData.append('image', newImageData.file);

                const updateResponse = await axios.patch(
                    `${API_BASE_URL}/customization-images/${cartItems[cartItemIndex].customization_image_id}/`,
                    formData,
                    {
                        headers: {
                            'Authorization': `Bearer ${accessToken}`,
                            'Content-Type': 'multipart/form-data'
                        }
                    }
                );

                if (updateResponse.data.success) {
                    // Update local state with new customization image URL only
                    const updatedCartItems = [...cartItems];
                    updatedCartItems[cartItemIndex] = {
                        ...updatedCartItems[cartItemIndex],
                        customization_image_url: updateResponse.data.data.image || updatedCartItems[cartItemIndex].customization_image_url
                    };
                    setCartItems(updatedCartItems);

                    setEditingImageFor(null);
                    setNewImages(prev => {
                        const updated = { ...prev };
                        delete updated[cartItemId];
                        return updated;
                    });

                    // Optionally show a subtle UI success indicator (toast/snackbar)
                    console.log('Image updated successfully!');
                }
            } else {
                // Create new image and link to cart
                const formData = new FormData();
                formData.append('image', newImageData.file);

                const uploadResponse = await axios.post(
                    `${API_BASE_URL}/customization-images/`,
                    formData,
                    {
                        headers: {
                            'Authorization': `Bearer ${accessToken}`,
                            'Content-Type': 'multipart/form-data'
                        }
                    }
                );

                if (uploadResponse.data.success) {
                    const newImageId = uploadResponse.data.data.image_id;

                    await axios.put(
                        `${API_BASE_URL}/cart/update-item/${cartItemId}/`,
                        { customization_image_id: newImageId },
                        {
                            headers: {
                                'Authorization': `Bearer ${accessToken}`
                            }
                        }
                    );

                    // Update local cart item state similarly by fetching or manual update
                    // For minimal change, you may refetch the whole cart, or
                    // Optimally update the local cart item with new image ID and URL

                    await fetchCart(); // You can keep or remove depending on UX needs

                    setEditingImageFor(null);
                    setNewImages(prev => {
                        const updated = { ...prev };
                        delete updated[cartItemId];
                        return updated;
                    });

                    // console log instead of alert
                    console.log('Image added successfully!');
                }
            }
        } catch (error) {
            console.error('Error updating image:', error);
            // Show subtle UI error indicator (toast/snackbar) instead of alert in production
        } finally {
            setUploadingImages(prev => ({ ...prev, [cartItemId]: false }));
        }
    };


    // ✅ NEW: Cancel image edit
    const cancelImageEdit = (cartItemId) => {
        setEditingImageFor(null);
        setNewImages(prev => {
            const updated = { ...prev };
            delete updated[cartItemId];
            return updated;
        });
    };

    // ✅ NEW: Open preview popup
    const openPreview = (imageUrl) => {
        setPreviewImage(imageUrl);
    };

    // ✅ NEW: Close preview popup
    const closePreview = () => {
        setPreviewImage(null);
    };

    const removeFromCart = async (itemId) => {
        if (!window.confirm('Are you sure you want to remove this item?')) return;

        try {
            const accessToken = localStorage.getItem('access_token');

            if (accessToken) {
                await axios.delete(
                    `${API_BASE_URL}/cart/remove-item/${itemId}/`,
                    {
                        headers: {
                            'Authorization': `Bearer ${accessToken}`
                        }
                    }
                );
            }

            const updatedItems = cartItems.filter(item => item.cart_item_id !== itemId);
            setCartItems(updatedItems);

            const newTotal = updatedItems.reduce((sum, item) =>
                sum + parseFloat(item.prices?.total_price || 0) * item.quantity, 0
            );
            setTotalPrice(newTotal);
            refreshCounts();

            alert("✅ Removed from cart");
        } catch (error) {
            console.error('Error removing item:', error);
            alert("Failed to remove from cart");
        }
    };

    const updateQuantity = async (itemId, newQuantity) => {
        if (newQuantity < 1) return;

        try {
            const accessToken = localStorage.getItem('access_token');

            if (accessToken) {
                await axios.put(
                    `${API_BASE_URL}/cart/update-item/${itemId}/`,
                    { quantity: newQuantity },
                    {
                        headers: {
                            'Authorization': `Bearer ${accessToken}`
                        }
                    }
                );
            }

            const updatedItems = cartItems.map(item => {
                if (item.cart_item_id === itemId) {
                    return { ...item, quantity: newQuantity };
                }
                return item;
            });

            setCartItems(updatedItems);

            const newTotal = updatedItems.reduce((sum, item) =>
                sum + parseFloat(item.prices?.total_price || 0) * item.quantity, 0
            );
            setTotalPrice(newTotal);

        } catch (error) {
            console.error('Error updating quantity:', error);
            alert("Failed to update quantity");
        }
    };

    const calculateSubtotal = (item) => {
        const price = parseFloat(item.prices?.total_price || 0);
        return (price * item.quantity).toFixed(2);
    };

    const getCustomizationPreview = (customizations) => {
        if (!customizations) return [];

        const preview = [];

        if (customizations.width && customizations.height) {
            preview.push({
                label: 'Size',
                value: `${customizations.width}" × ${customizations.height}"`,
                id: 'size'
            });
        }

        if (customizations.fields && customizations.fields.length > 0) {
            customizations.fields.slice(0, 2).forEach((field, idx) => {
                preview.push({
                    label: field.name,
                    value: field.selected_value || 'N/A',
                    id: `field-${idx}`
                });
            });
        }

        return preview;
    };

    const getTotalCustomizationCount = (customizations) => {
        let count = 1;
        if (customizations?.fields) {
            count += customizations.fields.length;
        }
        return count;
    };

    const toggleExpandCustomizations = (itemId) => {
        setExpandedItems(prev => ({
            ...prev,
            [itemId]: !prev[itemId]
        }));
    };

    if (isLoading) {
        return (
            <section className="cart py-80">
                <div className="container container-lg">
                    <div className="text-center">
                        <div className="spinner mb-16"></div>
                        <p>Loading your cart...</p>
                    </div>
                </div>
            </section>
        );
    }

    if (error) {
        return (
            <section className="cart py-80">
                <div className="container container-lg">
                    <div className="text-center">
                        <i className="ph ph-warning-circle text-48 text-danger-600 mb-16 d-block"></i>
                        <p className="text-danger-600 mb-20">{error}</p>
                        <Link to="/profile" className="btn btn-main">Login to View Cart</Link><br /><br /><br /><br /><br /><br /><br /><br /><br />
                    </div>
                </div>
            </section>
        );
    }

    if (cartItems.length === 0) {
        return (
            <section className="cart py-80">
                <div className="container container-lg">
                    <div className="text-center">
                        <i className="ph ph-shopping-cart-simple text-48 text-orange-600 mb-16 d-block"></i>
                        <h3 className="text-2xl fw-bold mb-8">Your Cart is Empty</h3><br /><br /><br /><br /><br /><br /><br />
                        <p className="text-gray-600 mb-20">Start adding items to your cart!</p>
                        <Link to="/" className="btn btn-main">Continue Shopping</Link>
                    </div>
                </div>
            </section>
        );
    }

    return (
        <>
            {/* ✅ Image Preview Popup */}
            {previewImage && (
                <div
                    style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: 'rgba(0,0,0,0.8)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 9999,
                        cursor: 'pointer'
                    }}
                    onClick={closePreview}
                >
                    <div style={{ position: 'relative', maxWidth: '90%', maxHeight: '90%' }}>
                        <img
                            src={previewImage}
                            alt="Preview"
                            style={{
                                maxWidth: '100%',
                                maxHeight: '90vh',
                                borderRadius: '8px',
                                boxShadow: '0 4px 20px rgba(0,0,0,0.5)'
                            }}
                        />
                        <button
                            onClick={closePreview}
                            style={{
                                position: 'absolute',
                                top: '10px',
                                right: '10px',
                                backgroundColor: '#f44336',
                                color: 'white',
                                border: 'none',
                                borderRadius: '50%',
                                width: '40px',
                                height: '40px',
                                fontSize: '1.5rem',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                            }}
                        >
                            ×
                        </button>
                    </div>
                </div>
            )}

            <section className="cart py-80">
                <div className="container container-lg">
                    <div className="row gy-4">
                        <div className="col-lg-12">
                            <div className="cart-table border border-gray-100 rounded-8">
                                <div className="overflow-x-auto scroll-sm scroll-sm-horizontal">
                                    <table className="table rounded-8 overflow-hidden">
                                        <thead>
                                            <tr className="border-bottom border-neutral-100">
                                                <th className="h6 mb-0 text-lg fw-bold px-40 py-32 border-end border-neutral-100">Delete</th>
                                                <th className="h6 mb-0 text-lg fw-bold px-40 py-32 border-end border-neutral-100">Product & Material</th>
                                                <th className="h6 mb-0 text-lg fw-bold px-40 py-32 border-end border-neutral-100">Customizations</th>
                                                {/* <th className="h6 mb-0 text-lg fw-bold px-40 py-32 border-end border-neutral-100">Price</th> */}
                                                <th className="h6 mb-0 text-lg fw-bold px-40 py-32 border-end border-neutral-100">Quantity</th>
                                                <th className="h6 mb-0 text-lg fw-bold px-40 py-32 border-end border-neutral-100">Customization Image</th>
                                                <th className="h6 mb-0 text-lg fw-bold px-40 py-32">Subtotal</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {cartItems.map((item) => {
                                                const isExpanded = expandedItems[item.cart_item_id];
                                                const previewCustomizations = getCustomizationPreview(item.customizations);
                                                const totalCustomizations = getTotalCustomizationCount(item.customizations);
                                                const hiddenCount = totalCustomizations - previewCustomizations.length;
                                                const isEditingImage = editingImageFor === item.cart_item_id;
                                                const newImageData = newImages[item.cart_item_id];
                                                const isUploading = uploadingImages[item.cart_item_id];

                                                return (
                                                    <tr key={item.cart_item_id} className="border-bottom border-neutral-100">
                                                        <td className="px-40 py-32 border-end border-neutral-100">
                                                            <button
                                                                type="button"
                                                                className="remove-tr-btn flex-align gap-12 hover-text-danger-600"
                                                                onClick={() => removeFromCart(item.cart_item_id)}
                                                                title="Remove from cart"
                                                            >
                                                                <i className="ph ph-x-circle text-2xl d-flex" />
                                                                Remove
                                                            </button>
                                                        </td>

                                                        <td className="px-40 py-32 border-end border-neutral-100">
                                                            <div className="table-product d-flex align-items-center gap-24">
                                                                <Link
                                                                    to={`/product-details-material?type=${item.product_type.id}&material=${item.material.id}`}
                                                                    className="table-product__thumb border border-gray-100 rounded-8 flex-center"
                                                                >
                                                                    <img
                                                                        src={getImageUrl(item.material.image_swatch)}
                                                                        alt={item.material.name}
                                                                    />
                                                                </Link>
                                                                <div className="table-product__content text-start">
                                                                    <h6 className="title text-lg fw-semibold mb-8">
                                                                        <Link
                                                                            to={`/product-details-material?type=${item.product_type.id}&material=${item.material.id}`}
                                                                            className="link text-line-2"
                                                                        >
                                                                            {item.product_type.name}
                                                                        </Link>
                                                                    </h6>
                                                                    <p className="text-sm text-gray-600 mb-0">{item.material.name}</p>
                                                                </div>
                                                            </div>
                                                        </td>



                                                        <td className="px-40 py-32 border-end border-neutral-100">
                                                            <div className="customizations-display">
                                                                {item.customizations ? (
                                                                    <>
                                                                        <div className="customizations-preview">
                                                                            {previewCustomizations.map((custom) => (
                                                                                <div key={custom.id} className="custom-row">
                                                                                    <span className="custom-label">{custom.label}:</span>
                                                                                    <span className="custom-value">{custom.value}</span>
                                                                                </div>
                                                                            ))}
                                                                        </div>

                                                                        {isExpanded && hiddenCount > 0 && (
                                                                            <div className="customizations-expanded">
                                                                                {item.customizations.fields && item.customizations.fields.slice(2).map((field, idx) => (
                                                                                    <div key={`expanded-${idx}`} className="custom-row">
                                                                                        <span className="custom-label">{field.name}:</span>
                                                                                        <span className="custom-value">{field.selected_value || 'N/A'}</span>
                                                                                    </div>
                                                                                ))}
                                                                            </div>
                                                                        )}

                                                                        {hiddenCount > 0 && (
                                                                            <button
                                                                                className="btn-view-more"
                                                                                onClick={() => toggleExpandCustomizations(item.cart_item_id)}
                                                                            >
                                                                                {isExpanded ? (
                                                                                    <>
                                                                                        <i className="ph ph-caret-up"></i>
                                                                                        View Less
                                                                                    </>
                                                                                ) : (
                                                                                    <>
                                                                                        <i className="ph ph-caret-down"></i>
                                                                                        +{hiddenCount} more
                                                                                    </>
                                                                                )}
                                                                            </button>
                                                                        )}
                                                                    </>
                                                                ) : (
                                                                    <p className="text-gray-500">No customizations</p>
                                                                )}
                                                            </div>
                                                        </td>

                                                        {/* <td className="px-40 py-32 border-end border-neutral-100">
                                                            <div className="price-breakdown">
                                                                <div className="price-row">
                                                                    <span className="price-label">Base:</span>
                                                                    <span className="price-value">${parseFloat(item.prices?.base_price || 0).toFixed(2)}</span>
                                                                </div>
                                                                <div className="price-row">
                                                                    <span className="price-label">Material:</span>
                                                                    <span className="price-value">${parseFloat(item.prices?.material_price || 0).toFixed(2)}</span>
                                                                </div>
                                                                {parseFloat(item.prices?.customization_price || 0) > 0 && (
                                                                    <div className="price-row">
                                                                        <span className="price-label">Customization:</span>
                                                                        <span className="price-value">${parseFloat(item.prices?.customization_price || 0).toFixed(2)}</span>
                                                                    </div>
                                                                )}
                                                                <div className="price-row price-total">
                                                                    <span className="price-label fw-bold">Total:</span>
                                                                    <span className="price-value fw-bold">${parseFloat(item.prices?.total_price || 0).toFixed(2)}</span>
                                                                </div>
                                                            </div>
                                                        </td> */}

                                                        <td className="px-40 py-32 border-end border-neutral-100">
                                                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                                <button
                                                                    type="button"
                                                                    onClick={() => updateQuantity(item.cart_item_id, item.quantity - 1)}
                                                                    disabled={item.quantity <= 1}
                                                                    style={{
                                                                        padding: '4px 8px',
                                                                        border: '1px solid #ddd',
                                                                        borderRadius: '4px',
                                                                        cursor: item.quantity <= 1 ? 'not-allowed' : 'pointer',
                                                                        opacity: item.quantity <= 1 ? 0.5 : 1,
                                                                        backgroundColor: '#fff'
                                                                    }}
                                                                >
                                                                    −
                                                                </button>
                                                                <input
                                                                    type="number"
                                                                    value={item.quantity}
                                                                    readOnly
                                                                    style={{
                                                                        width: '40px',
                                                                        textAlign: 'center',
                                                                        border: '1px solid #ddd',
                                                                        borderRadius: '4px',
                                                                        padding: '4px'
                                                                    }}
                                                                />
                                                                <button
                                                                    type="button"
                                                                    onClick={() => updateQuantity(item.cart_item_id, item.quantity + 1)}
                                                                    style={{
                                                                        padding: '4px 8px',
                                                                        border: '1px solid #ddd',
                                                                        borderRadius: '4px',
                                                                        cursor: 'pointer',
                                                                        backgroundColor: '#fff'
                                                                    }}
                                                                >
                                                                    +
                                                                </button>
                                                            </div>
                                                        </td>
                                                        {/* ✅ NEW: Customization Image Column */}
                                                        <td className="px-40 py-32 border-end border-neutral-100">
                                                            <div className="customization-image-section">
                                                                {!isEditingImage ? (
                                                                    // Display mode
                                                                    <div style={{ textAlign: 'center' }}>
                                                                        {item.customization_image_url ? (
                                                                            <>
                                                                                <img
                                                                                    src={item.customization_image_url}
                                                                                    alt="Customization"
                                                                                    style={{
                                                                                        maxWidth: '100px',
                                                                                        maxHeight: '100px',
                                                                                        borderRadius: '8px',
                                                                                        marginBottom: '8px',
                                                                                        cursor: 'pointer',
                                                                                        boxShadow: '0 2px 6px rgba(0,0,0,0.1)'
                                                                                    }}
                                                                                    onClick={() => openPreview(item.customization_image_url)}
                                                                                />
                                                                                <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
                                                                                    <button
                                                                                        onClick={() => openPreview(item.customization_image_url)}
                                                                                        style={{
                                                                                            padding: '4px 12px',
                                                                                            fontSize: '0.75rem',
                                                                                            backgroundColor: '#2196f3',
                                                                                            color: 'white',
                                                                                            border: 'none',
                                                                                            borderRadius: '4px',
                                                                                            cursor: 'pointer'
                                                                                        }}
                                                                                    >
                                                                                        <i className="ph ph-eye"></i> Preview
                                                                                    </button>
                                                                                    <button
                                                                                        onClick={() => setEditingImageFor(item.cart_item_id)}
                                                                                        style={{
                                                                                            padding: '4px 12px',
                                                                                            fontSize: '0.75rem',
                                                                                            backgroundColor: '#ff9800',
                                                                                            color: 'white',
                                                                                            border: 'none',
                                                                                            borderRadius: '4px',
                                                                                            cursor: 'pointer'
                                                                                        }}
                                                                                    >
                                                                                        <i className="ph ph-pencil"></i> Edit
                                                                                    </button>
                                                                                </div>
                                                                            </>
                                                                        ) : (
                                                                            <div>
                                                                                <p style={{ color: '#999', fontSize: '0.85rem', marginBottom: '8px' }}>No image</p>
                                                                                <button
                                                                                    onClick={() => setEditingImageFor(item.cart_item_id)}
                                                                                    style={{
                                                                                        padding: '6px 16px',
                                                                                        fontSize: '0.8rem',
                                                                                        backgroundColor: '#4caf50',
                                                                                        color: 'white',
                                                                                        border: 'none',
                                                                                        borderRadius: '4px',
                                                                                        cursor: 'pointer'
                                                                                    }}
                                                                                >
                                                                                    <i className="ph ph-plus"></i> Add Image
                                                                                </button>
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                ) : (
                                                                    // Edit mode
                                                                    <div style={{ textAlign: 'center' }}>
                                                                        <input
                                                                            type="file"
                                                                            id={`edit-image-${item.cart_item_id}`}
                                                                            accept="image/*"
                                                                            onChange={(e) => handleImageEdit(e, item.cart_item_id)}
                                                                            style={{ display: 'none' }}
                                                                        />

                                                                        {!newImageData ? (
                                                                            <label
                                                                                htmlFor={`edit-image-${item.cart_item_id}`}
                                                                                style={{
                                                                                    display: 'block',
                                                                                    padding: '20px',
                                                                                    border: '2px dashed #90caf9',
                                                                                    borderRadius: '8px',
                                                                                    cursor: 'pointer',
                                                                                    marginBottom: '8px'
                                                                                }}
                                                                            >
                                                                                <i className="ph ph-cloud-arrow-up" style={{ fontSize: '2rem', color: '#1976d2' }}></i>
                                                                                <p style={{ margin: 0, fontSize: '0.8rem' }}>Select Image</p>
                                                                            </label>
                                                                        ) : (
                                                                            <div style={{ marginBottom: '8px' }}>
                                                                                <img
                                                                                    src={newImageData.preview}
                                                                                    alt="New"
                                                                                    style={{
                                                                                        maxWidth: '100px',
                                                                                        maxHeight: '100px',
                                                                                        borderRadius: '8px',
                                                                                        marginBottom: '8px'
                                                                                    }}
                                                                                />
                                                                            </div>
                                                                        )}

                                                                        <div style={{ display: 'flex', gap: '4px', justifyContent: 'center' }}>
                                                                            <button
                                                                                onClick={() => uploadAndUpdateImage(item.cart_item_id)}
                                                                                disabled={!newImageData || isUploading}
                                                                                style={{
                                                                                    padding: '4px 12px',
                                                                                    fontSize: '0.75rem',
                                                                                    backgroundColor: '#4caf50',
                                                                                    color: 'white',
                                                                                    border: 'none',
                                                                                    borderRadius: '4px',
                                                                                    cursor: !newImageData || isUploading ? 'not-allowed' : 'pointer',
                                                                                    opacity: !newImageData || isUploading ? 0.5 : 1
                                                                                }}
                                                                            >
                                                                                {isUploading ? 'Saving...' : 'Save'}
                                                                            </button>
                                                                            <button
                                                                                onClick={() => cancelImageEdit(item.cart_item_id)}
                                                                                disabled={isUploading}
                                                                                style={{
                                                                                    padding: '4px 12px',
                                                                                    fontSize: '0.75rem',
                                                                                    backgroundColor: '#f44336',
                                                                                    color: 'white',
                                                                                    border: 'none',
                                                                                    borderRadius: '4px',
                                                                                    cursor: isUploading ? 'not-allowed' : 'pointer',
                                                                                    opacity: isUploading ? 0.5 : 1
                                                                                }}
                                                                            >
                                                                                Cancel
                                                                            </button>
                                                                        </div>
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </td>
                                                        <td className="px-40 py-32">
                                                            <span className="text-lg h6 mb-0 fw-semibold">
                                                                ${calculateSubtotal(item)}
                                                            </span>
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>

                                <div className="flex-between flex-wrap gap-16 mt-16 px-40 py-16">
                                    <div className="flex-align gap-16"></div>
                                    <button
                                        type="button"
                                        className="text-lg text-gray-500 hover-text-main-600"
                                        onClick={fetchCart}
                                    >
                                        Refresh Cart
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="row mt-32">
                        <div className="col-lg-4 ms-auto">
                            <div className="border border-gray-100 rounded-8 px-40 py-32">
                                <h6 className="text-xl mb-32">Cart Totals</h6>

                                <div className="bg-color-three rounded-8 p-24 mb-24">
                                    <div className="mb-32 flex-between gap-8 pb-24 border-bottom border-neutral-100">
                                        <span className="text-lg fw-semibold">Subtotal ({cartItems.length} items):</span>
                                        <span className="text-lg h6 mb-0 fw-bold text-orange-600">${totalPrice.toFixed(2)}</span>
                                    </div>
                                    <div className="mb-32 flex-between gap-8">
                                        <span className="text-gray-900 font-heading-two">
                                            Estimated Delivery
                                        </span>
                                        <span className="text-gray-900 fw-semibold">Free</span>
                                    </div>
                                    <div className="mb-0 flex-between gap-8">
                                        <span className="text-gray-900 font-heading-two">
                                            Estimated Taxes
                                        </span>
                                        <span className="text-gray-900 fw-semibold">USD 10.00</span>
                                    </div>
                                </div>

                                <div className="bg-color-three rounded-8 p-24">
                                    <div className="flex-between gap-8">
                                        <span className="text-gray-900 text-xl fw-semibold">Total</span>
                                        <span className="text-gray-900 text-xl fw-semibold">
                                            ${(parseFloat(totalPrice) + 10).toFixed(2)}
                                        </span>
                                    </div>
                                </div>

                                <Link
                                    to="/checkout"
                                    className="btn btn-main mt-40 py-18 w-100 rounded-8"
                                >
                                    Checkout
                                </Link>
                                <Link
                                    to="/"
                                    className="btn btn-outline-main mt-16 py-18 w-100 rounded-8"
                                >
                                    Continue Shopping
                                </Link>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        </>
    );
};

export default CartSection;
